/**
 * Shrinks a camera photo before upload. Phone cameras produce 5-12MB images, which are slow to
 * upload on mobile data and far larger than the visual search or the item photo needs.
 *
 * Best-effort by design: if the browser cannot decode the file (HEIC, say) or lacks canvas
 * encoding, the original file is returned and the upload proceeds as before.
 */

const MAX_DIMENSION = 1600
const JPEG_QUALITY = 0.85

export async function downscaleImage(file: File): Promise<File> {
  if (!file.type.startsWith('image/')) return file
  if (typeof createImageBitmap !== 'function') return file

  let bitmap: ImageBitmap
  try {
    // `from-image` applies EXIF orientation, so the drawn photo is the right way up.
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch {
    return file
  }

  try {
    const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.round(bitmap.width * scale)
    canvas.height = Math.round(bitmap.height * scale)

    const context = canvas.getContext('2d')
    if (!context) return file
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height)

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY)
    })
    // Re-encoding can grow an already-small or better-compressed file — keep whichever is smaller.
    if (!blob || blob.size >= file.size) return file

    return new File([blob], toJpegName(file.name), { type: 'image/jpeg' })
  } catch {
    return file
  } finally {
    bitmap.close()
  }
}

function toJpegName(name: string): string {
  const base = name.replace(/\.[^./\\]+$/, '') || 'photo'
  return `${base}.jpg`
}
